import { showDeleteConfirmation } from '@modules/common';
import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { handleMessage, requestMessage } from '@business/messages';
import {
  ProposalTemplateId,
  ProposalTemplateResponse,
  ProposalTemplateFolderId,
  ProposalTemplateStatus,
  CopyTemplateRequest,
} from '@business/entities/proposal';
import { ProposalTemplateAPI } from '@business/api/proposal_template_api';
import { switchMap, tap } from 'rxjs/operators';

export class ProposalTemplateBloc extends BaseBloc {
  private readonly templatesRepo = new Repository<ProposalTemplateResponse>({
    getItemId: brand => brand.id,
  });

  readonly templates$ = this.templatesRepo.items$;
  readonly selectItem = this.templatesRepo.selectItem;

  onReset() {
    this.templatesRepo.reset();
  }

  fetchTemplates = (full = false, status = 'active', folderId?: ProposalTemplateFolderId) => {
    return ProposalTemplateAPI.fetchTemplates(full, status, folderId).pipe(
      this.templatesRepo.ops.reset(),
      handleMessage({ error: requestMessage('fetch_proposal_template_error') }),
    );
  };

  fetchTemplate = (id: ProposalTemplateId) => {
    return ProposalTemplateAPI.fetchTemplate(id).pipe(
      this.templatesRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_proposal_template_error') }),
    );
  };

  getItem = (id: ProposalTemplateId) => {
    return ProposalTemplateAPI.fetchTemplate(id).pipe(
      this.templatesRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_proposal_template_error') }),
    );
  };

  createTemplate = (data: Partial<ProposalTemplateResponse> & { libraryId?: string }) => {
    return ProposalTemplateAPI.createTemplate(data).pipe(
      this.templatesRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_proposal_template'),
      }),
    );
  };

  duplicateTemplate = (id: ProposalTemplateId, name: string) => {
    return ProposalTemplateAPI.duplicateTemplate(id, name).pipe(
      this.templatesRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_proposal_template'),
      }),
    );
  };

  updateTemplate = (id: ProposalTemplateId, data: Partial<ProposalTemplateResponse>) => {
    return ProposalTemplateAPI.updateTemplate(id, data).pipe(
      this.templatesRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_proposal_template'),
      }),
    );
  };

  deleteTemplate = (id: ProposalTemplateId, archived = false) => {
    const title = archived ? 'Unarchive Template' : 'Archive Template';
    const message = archived ? 'Do you really want to unarchive this template?' : 'Do you really want to archive this template?';
    return showDeleteConfirmation(title, message).pipe(
      switchMap(() => ProposalTemplateAPI.deleteTemplate(id)),
      tap(item => {
        if (item) {
          return this.templatesRepo.upsertOne({ item });
        } else {
          return this.templatesRepo.removeOne(id);
        }
      }),
      handleMessage({
        type: archived ? requestMessage('undelete_proposal_template') : requestMessage('delete_proposal_template'),
      }),
    );
  };

  updateFormData = (id: string, data: string) => {
    return ProposalTemplateAPI.updateFormData(id, data).pipe(
      this.templatesRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        error: requestMessage('fetch_proposal_template_error'),
      }),
    );
  };

  moveTemplate = (id: ProposalTemplateId, folderId: ProposalTemplateFolderId) => {
    return ProposalTemplateAPI.moveTemplate(id, folderId).pipe(
      this.templatesRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_proposal_template'),
      }),
    );
  };

  changeStatus = (id: ProposalTemplateId, status: ProposalTemplateStatus) => {
    return ProposalTemplateAPI.changeStatus(id, status).pipe(
      this.templatesRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('change_status_template'),
      }),
    );
  };

  copyBlockTemplate = (data: CopyTemplateRequest) => {
    return ProposalTemplateAPI.copyBlockTemplate(data).pipe(
      handleMessage({
        type: requestMessage('copy_proposal_template_block'),
      }),
    );
  };
}
